import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LogOut, Settings, Menu, HelpCircle, FileText, Shield, X, User, Users } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { NotificationBell } from './NotificationBell';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ProfileModal } from './ProfileModal';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';
import { Button } from './ui/button';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface HeaderProps {
  breadcrumbs?: BreadcrumbItem[];
  onCustomizationClick?: () => void;
}

export function Header({ breadcrumbs, onCustomizationClick }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAdmin = user?.role === 'admin';
  const [showAdminBanner, setShowAdminBanner] = useState(false);

  // Controle do banner de admin - aparece a cada login
  useEffect(() => {
    if (isAdmin) {
      const lastLoginId = localStorage.getItem('admin_banner_login_id');
      const currentLoginId = user?.id?.toString() + '_' + (user as any)?.lastLoginAt;

      if (lastLoginId !== currentLoginId) {
        setShowAdminBanner(true);
      }
    }
  }, [isAdmin, user]);

  const dismissAdminBanner = () => {
    const currentLoginId = user?.id?.toString() + '_' + (user as any)?.lastLoginAt;
    localStorage.setItem('admin_banner_login_id', currentLoginId);
    setShowAdminBanner(false);
  };

  const handleNotificationOpenChange = (open: boolean) => {
    setNotificationOpen(open);
    if (open && userMenuOpen) {
      setUserMenuOpen(false);
    }
  };

  const handleUserMenuOpenChange = (open: boolean) => {
    setUserMenuOpen(open);
    if (open && notificationOpen) {
      setNotificationOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getUserInitials = () => {
    if (!user?.email) return 'U';
    const parts = user.email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  return (
    <>
      {/* Banner de Admin */}
      {isAdmin && showAdminBanner && (
        <div className="bg-gradient-to-r from-red-600 to-red-500 text-white px-4 py-2 relative z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
            <Shield className="h-4 w-4" />
            <span className="text-sm font-medium">
              {tCommon('header.logged_as_admin')}
            </span>
            <button
              onClick={dismissAdminBanner}
              className="absolute right-4 p-1 hover:bg-white/20 rounded transition-colors"
              aria-label={tCommon('header.close_warning')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img
              src="/logo-stant.svg"
              alt="Stant"
              className="w-28 h-auto dark:brightness-0 dark:invert"
            />
          </Link>

          {/* Tabs de navegação entre sistemas (só para admins) */}
          {isAdmin && (
            <nav className="hidden sm:flex items-center gap-1 ml-6">
              <button
                onClick={() => navigate('/dashboard')}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  !isAdminRoute
                    ? 'bg-secondary text-secondary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <FileText className="h-4 w-4" />
                {tCommon('header.system_atas')}
              </button>
              <button
                onClick={() => navigate('/admin')}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  isAdminRoute
                    ? 'bg-secondary text-secondary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Shield className="h-4 w-4" />
                {tCommon('header.system_admin')}
              </button>
            </nav>
          )}

          {/* Ações do lado direito */}
          <div className="flex items-center gap-1">
            {/* Como utilizar */}
            <button
              onClick={() => navigate('/docs')}
              className="relative flex items-center gap-1.5 px-3 h-9 rounded-full hover:bg-muted transition-colors text-sm text-muted-foreground"
              aria-label={t('login.how_to_use')}
            >
              <HelpCircle size={16} />
              <span className="hidden sm:inline font-medium">{t('login.how_to_use')}</span>
            </button>

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Notification Bell */}
            <NotificationBell
              open={notificationOpen}
              onOpenChange={handleNotificationOpenChange}
            />

            {/* User Menu */}
            <DropdownMenu
              open={userMenuOpen}
              onOpenChange={handleUserMenuOpenChange}
            >
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10 border-2 border-border">
                    {user?.avatar_url && (
                      <AvatarImage src={user.avatar_url} alt={user.name} />
                    )}
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{t('my_account')}</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setProfileModalOpen(true)}>
                  <User className="mr-2 h-4 w-4" />
                  <span>{t('profile')}</span>
                </DropdownMenuItem>
                {onCustomizationClick && (
                  <DropdownMenuItem onClick={onCustomizationClick}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>{t('customization')}</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {}
            <Button variant="ghost" size="icon" className="md:hidden">
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </header>

      {/* Profile Modal */}
      <ProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />
    </>
  );
}
